import {combineReducers, configureStore} from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage'
import {createTransform, persistReducer, persistStore, PersistConfig} from 'redux-persist'

import * as system from './system';
import * as auth from './auth';
import * as users from './users';
import * as toast from './toast';
import * as bookings from './bookings';
import * as waitingBookings from './waiting-bookings';
import * as holidays from './holidays';
import * as customers from './customers';
import * as chats from './chats';
import * as communications from './communications';
import * as profiles from './profiles';
import * as profilePackages from './profile-packages';
import * as financialTransactions from './financial-transactions';
import * as slots from './slots';
import * as modalPages from './modal-pages';
import * as shop from './shop';
import * as orders from './orders';
import * as preferences from './preferences';
import * as upcomingEvents from './upcoming-events';
import * as branding from './branding';
import {FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE} from "redux-persist/es/constants";

/**
 * Reducer
 */
const reducer = combineReducers({
    [auth.type]: auth.reducer,
    [users.type]: users.reducer,
    [bookings.type]: bookings.reducer,
    [waitingBookings.type]: waitingBookings.reducer,
    [communications.type]: communications.reducer,
    [chats.type]: chats.reducer,
    [customers.type]: customers.reducer,
    [financialTransactions.type]: financialTransactions.reducer,
    [holidays.type]: holidays.reducer,
    [modalPages.type]: modalPages.reducer,
    [profiles.type]: profiles.reducer,
    [profilePackages.type]: profilePackages.reducer,
    [slots.type]: slots.reducer,
    [shop.type]: shop.reducer,
    [orders.type]: orders.reducer,
    [preferences.type]: preferences.reducer,
    [system.type]: system.reducer,
    [toast.type]: toast.reducer,
    [upcomingEvents.type]: upcomingEvents.reducer,
    [branding.type]: branding.reducer,
});

/**
 * MIddleware
 */
const middleware = {
    thunk: true,
    immutableCheck: true,
    serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
};

/**
 * Persist Store
 */

/**
 * shop.proformaStatus is a per-order transient: rehydrating a previous order's
 * canBook/bookableProfilePackageId on /payment-return could auto-book the wrong
 * package. isFetching/errorMessage are per-request transients: an app killed
 * mid-request would otherwise rehydrate isFetching:true, which disables the
 * checkout buttons and blocks the shop fetch gate until logout. Strip all
 * three on write, and on read so state persisted by older builds is discarded
 * too. cart/paymentUrl/proforma stay persisted (payment retry path). Exported
 * for tests.
 */
const stripShopTransients = (state: shop.ShopState): shop.ShopState => ({
    ...state,
    proformaStatus: undefined,
    isFetching: false,
    errorMessage: undefined,
});

export const shopPersistTransform = createTransform<shop.ShopState, shop.ShopState>(
    (inboundState) => stripShopTransients(inboundState),
    (outboundState) => stripShopTransients(outboundState),
    {whitelist: [shop.type]},
);

/**
 * modal-pages is blacklisted from persistence (modal open-flags are pure UI
 * state), but the blacklist only filters writes: a slice persisted by a build
 * prior to the blacklist would still rehydrate once and present a phantom
 * modal on boot. Discard any stored value on read. Exported for tests.
 */
export const modalPagesRehydrateTransform = createTransform<modalPages.ModalPageState, modalPages.ModalPageState>(
    (inboundState) => inboundState,
    () => ({...modalPages.initialState}),
    {whitelist: [modalPages.type]},
);

const persistConfig: PersistConfig<ReturnType<typeof reducer>> = {
    key: 'root',
    storage,
    // Ephemeral UI state must not survive a reload. Persisting the toast slice
    // could otherwise rehydrate a stale/empty toast on next launch, and a
    // persisted modal-pages open flag would present a phantom modal on boot.
    blacklist: [toast.type, branding.type, modalPages.type],
    transforms: [shopPersistTransform, modalPagesRehydrateTransform],
};


const persistedReducer = persistReducer(persistConfig, reducer);

/**
 * The unwrapped root reducer (no redux-persist wrapper). Exported additively for
 * tests, which build a non-persisted store via `createTestStore`. The runtime
 * store below continues to use the persisted reducer — behaviour is unchanged.
 */
export const rootReducer = reducer;

/**
 * Store
 */
export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware(middleware),
    devTools: process.env.NODE_ENV !== 'production',
});
export const persistor = persistStore(store)

/**
 * Types
 */
export type State = ReturnType<typeof reducer>;
export type AppDispatch = typeof store.dispatch;


